import React, { ReactNode, useContext, useEffect, useState } from 'react';

import DeviceSoftwareEndOfSupportContext from '@totem/components/devices/softwareEndOfLife/summary/DeviceSoftwareEndOfSupportContext';
import {
  DeviceSoftwareEndOfSupportInput,
  SoftwareEndOfSupportSummary,
} from '@totem/components/devices/softwareEndOfLife/summary/types';
import { buildParameters } from '@totem/components/devices/softwareEndOfLife/summary/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { omitNilOrEmpty } from '@totem/utilities/objectUtilities';

type Props = {
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  deviceId?: string[];
  children?: ReactNode;
  onRecordTotalChanged?: (count: number) => void;
  onRecordTotalNearChanged?: (count: number) => void;
};
const DeviceSoftwareEndOfSupportContainer = ({
  regionId,
  buildingId,
  controlSystemId,
  deviceId,
  children,
  onRecordTotalChanged,
  onRecordTotalNearChanged,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [input, updateInput] = useState<DeviceSoftwareEndOfSupportInput>({
    ...(regionId !== undefined && { regionId }),
    ...(buildingId !== undefined && { buildingId }),
    ...(controlSystemId !== undefined && { controlSystemId }),
    ...(deviceId !== undefined && { deviceId }),
  });
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<SoftwareEndOfSupportSummary>(null);
  const [action, setAction] = useState<string>('');
  const setInput = (updated: Partial<DeviceSoftwareEndOfSupportInput>) => {
    const newInput = omitNilOrEmpty({
      ...input,
      ...updated,
    }) as DeviceSoftwareEndOfSupportInput;

    updateInput(newInput);

    setRefreshData(true);
  };
  useEffect(() => {
    setRefreshData(true);
  }, [regionId, buildingId, controlSystemId, userProfile.organization.id]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const uri = `${DEVICES_ENDPOINT}/software/endOfSupport/summary`;
      const payload = buildParameters(input);
      fetch(uri, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((result: SoftwareEndOfSupportSummary) => {
          setData(result);

          if (isNotNull(onRecordTotalChanged)) {
            onRecordTotalChanged(result.endOfSupport);
          }
          if (isNotNull(onRecordTotalNearChanged)) {
            onRecordTotalNearChanged(result.futureEndOfSupportTotals);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);
  const handleAction = (newAction: string) => {
    switch (newAction) {
      case 'refresh':
        setRefreshData(true);

        setAction('');
        break;
      default:
        setAction(newAction);
    }
  };
  return (
    <DeviceSoftwareEndOfSupportContext.Provider
      value={{
        input,
        setInput,
        loading: isLoading,
        data,
        action,
        onAction: handleAction,
      }}
    >
      <div>{children}</div>
    </DeviceSoftwareEndOfSupportContext.Provider>
  );
};
export default DeviceSoftwareEndOfSupportContainer;
